import { Loader2Icon, PlusIcon } from 'lucide-react'
import { type ReactNode, useEffect } from 'react'

import { BillingPaymentMethodsSection } from '@/components/billing/billing-payment-methods-section.organism'
import { EmbeddedCheckoutDrawer } from '@/components/billing/embedded-checkout.organism'
import { Button } from '@/components/ui/button'
import { useEmbeddedCheckout } from '@/hooks/billing/use-embedded-checkout.hook'
import { usePaymentMethods } from '@/hooks/billing/use-payment-methods.hook'
import { handleCheckoutReturnParams } from '@/utils/checkout-return.util'

export const PaymentMethodsSettingsView: React.FC = (): ReactNode => {
  useEffect(() => {
    handleCheckoutReturnParams()
  }, [])
  const { checkoutState, openCheckout, closeCheckout, handleCheckoutComplete } =
    useEmbeddedCheckout()
  const { cardList, isLoading, error, setDefaultCard, deleteCard, addCard, isAddingCard, refetch } =
    usePaymentMethods((clientSecret) => {
      openCheckout(clientSecret)
    })

  return (
    <div className="payment-methods-settings-view">
      <section className="payment-methods-section">
        <PaymentMethodsHeader onAddCard={addCard} isAddingCard={isAddingCard} />
        <PaymentMethodsContent
          cardList={cardList}
          isLoading={isLoading}
          error={error}
          onSetDefault={setDefaultCard}
          onDelete={deleteCard}
        />
      </section>
      <EmbeddedCheckoutDrawer
        isOpen={checkoutState.isOpen}
        onOpenChange={(isOpen) => {
          if (!isOpen) closeCheckout()
        }}
        clientSecret={checkoutState.clientSecret}
        onComplete={() => {
          handleCheckoutComplete()
          void refetch()
        }}
      />
    </div>
  )
}

interface PaymentMethodsHeaderProps {
  onAddCard: () => void
  isAddingCard: boolean
}

const PaymentMethodsHeader: React.FC<PaymentMethodsHeaderProps> = ({ onAddCard, isAddingCard }) => (
  <div className="payment-methods-header mb-6 flex items-start justify-between gap-4">
    <div>
      <h3 className="text-lg font-medium">Payment Methods</h3>
      <p className="text-sm text-muted-foreground">
        Manage your payment methods for subscriptions and balance top-ups.
      </p>
    </div>
    <Button onClick={onAddCard} disabled={isAddingCard} className="payment-methods-add-button">
      {isAddingCard ? (
        <Loader2Icon className="mr-2 size-4 animate-spin" />
      ) : (
        <PlusIcon className="mr-2 size-4" />
      )}
      Add card
    </Button>
  </div>
)

interface PaymentMethodsContentProps {
  cardList: ReturnType<typeof usePaymentMethods>['cardList']
  isLoading: boolean
  error: string | null
  onSetDefault: (cardId: string) => void
  onDelete: (cardId: string) => void
}

const PaymentMethodsContent: React.FC<PaymentMethodsContentProps> = ({
  cardList,
  isLoading,
  error,
  onSetDefault,
  onDelete,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2Icon className="size-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return <p className="text-center text-destructive py-6">{error}</p>
  }

  return (
    <BillingPaymentMethodsSection
      cardList={cardList}
      onSetDefault={(card) => onSetDefault(card.id)}
      onDeleteCard={(card) => onDelete(card.id)}
    />
  )
}

PaymentMethodsSettingsView.displayName = 'PaymentMethodsSettingsView'
